import type {IDocumentData} from "@buildwithflux/models";
import {isEqual} from "lodash";

import {selectObjects} from "modules/document/state/actionCreators/documentActions";

import type {DocumentActionWithOptionalPayload} from "./types";

/**
 * Checks if an action is a selectObjects action.
 * Used to handle selection natively in Zustand without going through the Redux reducer.
 *
 * @param action The action to check
 * @returns True if the action is a selectObjects action
 */
export function isSelectObjectsAction(
    action: DocumentActionWithOptionalPayload,
): action is ReturnType<typeof selectObjects> {
    return action.type === selectObjects.type;
}

/**
 * Handles the selectObjects action natively in Zustand.
 * This bypasses the Redux reducer for better performance and cleaner architecture.
 *
 * Selection actions don't need:
 * - withHistoryUpdate (no patches for undo/redo - selection changes aren't undoable in the traditional sense)
 * - Action records (shouldGenerateActionRecord: false)
 * - Timestamp updates (updatesDocumentTimestamp: false)
 *
 * @param document The current document state (not mutated)
 * @param selectedObjectUids The UIDs of objects to select
 * @returns The updated document state, or the same reference if no change
 */
export function handleSelectObjectsNatively(
    document: IDocumentData | null,
    selectedObjectUids: string[],
): IDocumentData | null {
    if (!document) {
        return document;
    }

    // Check if selection actually changed using the same logic as updateSelectedSubjects
    // This avoids unnecessary object creation and re-renders
    const currentSelection = document.selectedObjectUids;

    // Fast path: if lengths differ, selection definitely changed
    if (currentSelection?.length !== selectedObjectUids.length) {
        return {...document, selectedObjectUids};
    }

    // Slow path: deep compare if lengths are the same
    if (!isEqual(currentSelection, selectedObjectUids)) {
        return {...document, selectedObjectUids};
    }

    // No change - return the same reference for memoization
    return document;
}
